import type { LocalAudioTrack, RemoteAudioTrack } from 'livekit-client';
import type { DeepFilterNoiseFilterProcessor } from 'deepfilternet3-noise-filter';

/**
 * ZAJSZURES a sajat mikrofonon - DeepFilterNet3 (docs/22-zajszures.md).
 *
 * A bongeszo beepitett zajszurese (`noiseSuppression: true`) csak az ALLANDO
 * zajt viszi: ventilator, zugas. A billentyuzet, egy becsapodo ajto vagy a
 * szomszed asztalnal beszelo kolléga atjut rajta. Ezt egy neuralis halo
 * (DeepFilterNet3) kezeli, ami a SAJAT gepen fut, egy AudioWorkletben - a
 * hang nem megy sehova feldolgozasra.
 *
 * A beepitett szurot NEM kapcsoljuk ki mellette: ha a modell nem indul el,
 * az marad meg tartaleknak, es ket szuro egymas utan nem rontja a hangot.
 *
 * **A modell es a WASM SAJAT kiszolgalonkrol jon** (`/deepfilternet/`), nem a
 * csomag alapertelmezett kulso CDN-jerol - lasd `scripts/fetch-deepfilternet.mjs`.
 */

export type NoiseLevel = 'off' | 'normal' | 'strong';

export const NOISE_LEVELS: readonly NoiseLevel[] = ['off', 'normal', 'strong'];

export const NOISE_LEVEL_LABEL: Record<NoiseLevel, string> = {
  off: 'Ki',
  normal: 'Normál',
  strong: 'Erős',
};

/**
 * A legnagyobb csillapitas dB-ben (a modell `atten_lim` parametere).
 *
 * NORMAL: a zaj 30 dB-lel halkul - a billentyuzet es a hatter alig hallhato,
 * de a sajat hang termeszetes marad.
 * EROS: nincs korlat (100 = a modell teljes erejevel) - a hatterbeszedet is
 * elnyomja; cserebe a sajat hang egy arnyalattal tompabb lehet.
 */
const ATTENUATION_DB: Record<Exclude<NoiseLevel, 'off'>, number> = {
  normal: 30,
  strong: 100,
};

/**
 * Gepenkent megjegyezve, mint a hatter.
 *
 * Alapbol BEKAPCSOLVA (Normál) - kiveve telefonon: ott a 24 MB-os letoltes
 * mobilneten, es a folyamatos processzorterheles az akkun tobbet art, mint
 * amennyit a szures hasznal. Aki akarja, ott is bekapcsolhatja.
 */
const STORAGE_KEY = 'avslack.huddle.noise';

function defaultLevel(): NoiseLevel {
  try {
    return window.matchMedia('(pointer: coarse)').matches ? 'off' : 'normal';
  } catch {
    return 'normal';
  }
}

export function noisePreference(): NoiseLevel {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    return NOISE_LEVELS.includes(raw as NoiseLevel) ? (raw as NoiseLevel) : defaultLevel();
  } catch {
    return defaultLevel();
  }
}

export function rememberNoise(level: NoiseLevel): void {
  try {
    localStorage.setItem(STORAGE_KEY, level);
  } catch {
    // A tarolas hianya nem torheti el a hivast.
  }
}

/** A modell fajljai a sajat kiszolgalon: `/deepfilternet/v3/pkg/...` es `/models/...`. */
const ASSET_BASE = '/deepfilternet';

/**
 * A zajszures beallitasa az elo mikrofonsavon. Azt adja vissza, ami TENYLEG
 * ervenybe lepett.
 *
 * SOSEM dob: ha a modell nem tolt be (regi gep, hianyzo SIMD-tamogatas,
 * elerhetetlen fajl), a mikrofon akkor is megy tovabb - a bongeszo sajat
 * zajszuresevel. Egy nem mukodo szurotol nem eshet szet a hivas.
 *
 * Ha mar fut a processzor, csak ATALLITJUK (szint, ki/be): egy uj processzor
 * felepitese a hangban egy kattanast es par szaz ms csendet okozna.
 */
export async function applyNoiseFilter(
  track: LocalAudioTrack,
  level: NoiseLevel,
): Promise<NoiseLevel> {
  try {
    const current = track.getProcessor() as DeepFilterNoiseFilterProcessor | undefined;
    const running = current?.name === PROCESSOR_NAME ? current : undefined;

    if (level === 'off') {
      // Leallitjuk, nem csak megkerüljük: kikapcsolva ne egyen processzort.
      if (running) await track.stopProcessor();
      return 'off';
    }

    if (running) {
      running.setSuppressionLevel(ATTENUATION_DB[level]);
      await running.setEnabled(true);
      return level;
    }

    // A csomag (es a 24 MB-os modell) csak az elso hasznalatkor toltodik be.
    const { DeepFilterNoiseFilter, DeepFilterNoiseFilterProcessor } =
      await import('deepfilternet3-noise-filter');
    if (!DeepFilterNoiseFilterProcessor.isSupported()) return 'off';

    const processor = DeepFilterNoiseFilter({
      sampleRate: 48000,
      noiseReductionLevel: ATTENUATION_DB[level],
      assetConfig: { cdnUrl: new URL(ASSET_BASE, window.location.origin).href },
    });
    processor.name = PROCESSOR_NAME;
    await track.setProcessor(processor);
    return level;
  } catch (err) {
    console.warn('[huddle] a zajszuro nem indult el, a bongeszo szurese marad', err);
    await track.stopProcessor().catch(() => undefined);
    return 'off';
  }
}

const PROCESSOR_NAME = 'avslack-deepfilternet3';

// ─── A telefonos hivo hangja ──────────────────────────────────────────────────

/** Egy tavoli savon futo szuro - a leallitasahoz. */
export interface RemoteNoiseFilter {
  setLevel(level: Exclude<NoiseLevel, 'off'>): void;
  stop(): void;
}

/**
 * ZAJSZURES A TELEFONOS HIVO HANGJAN - a hallgato bongeszojeben.
 *
 * A telefonvonal a szerveren at jon (LiveKit SIP), ott nincs hova modellt
 * tenni. A lejatszas viszont nalunk Web Audio-n megy (`webAudioMix`), es a
 * LiveKit a tavoli sav ele beengedi a sajat csomopontjainkat
 * (`setWebAudioPlugins`) - ide kerul ugyanaz a modell, mint a sajat mikrofonra.
 *
 * Csak 48 kHz-es hangkornyezetben: a modell arra keszult, mas mintavetelen
 * rosszabb lenne, mint semmi. `null`, ha nem indult el - ilyenkor a hang
 * valtozatlanul szol.
 */
export async function applyRemoteNoiseFilter(
  track: RemoteAudioTrack,
  level: Exclude<NoiseLevel, 'off'>,
): Promise<RemoteNoiseFilter | null> {
  try {
    // A LiveKit sajat (lejatszo) hangkornyezete - a csomopontnak ebben kell lennie.
    const context = (track as unknown as { audioContext?: AudioContext }).audioContext;
    if (!context || context.sampleRate !== 48000) return null;

    const { DeepFilterNet3Core, DeepFilterNoiseFilterProcessor } =
      await import('deepfilternet3-noise-filter');
    if (!DeepFilterNoiseFilterProcessor.isSupported()) return null;
    const core = new DeepFilterNet3Core({
      sampleRate: 48000,
      noiseReductionLevel: ATTENUATION_DB[level],
      assetConfig: { cdnUrl: new URL(ASSET_BASE, window.location.origin).href },
    });
    await core.initialize();
    const node = await core.createAudioWorkletNode(context);
    track.setWebAudioPlugins([node]);
    return {
      setLevel: (next) => core.setSuppressionLevel(ATTENUATION_DB[next]),
      stop: () => {
        track.setWebAudioPlugins([]);
        node.disconnect();
        core.destroy();
      },
    };
  } catch (err) {
    console.warn('[huddle] a telefonos hang zajszurese nem indult el', err);
    return null;
  }
}
